import { useMemo, useSyncExternalStore } from "react";
import { invoiceStore } from "./index";
import type {
  Invoice,
  InvoiceDirection,
  InvoiceStatus,
  InvoiceStore,
} from "./types";

function getSnapshot(): Invoice[] {
  return invoiceStore.list();
}

const SERVER_SNAPSHOT: Invoice[] = [];
function getServerSnapshot(): Invoice[] {
  return SERVER_SNAPSHOT;
}

function subscribe(listener: () => void): () => void {
  return invoiceStore.subscribe(listener);
}

export interface UseInvoicesFilter {
  status?: InvoiceStatus[];
  direction?: InvoiceDirection;
}

export interface UseInvoicesResult {
  invoices: Invoice[];
  add: InvoiceStore["add"];
  addMany: InvoiceStore["addMany"];
  update: InvoiceStore["update"];
  setStatus: InvoiceStore["setStatus"];
  remove: InvoiceStore["remove"];
  submit: InvoiceStore["submit"];
  approve: InvoiceStore["approve"];
  reject: InvoiceStore["reject"];
  getById(id: string): Invoice | undefined;
}

export function useInvoices(filter?: UseInvoicesFilter): UseInvoicesResult {
  const all = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const statusKey = filter?.status?.slice().sort().join("|") ?? "";
  const directionKey = filter?.direction ?? "";

  const invoices = useMemo(() => {
    let out = all;
    if (filter?.status) {
      const allowed = new Set(filter.status);
      out = out.filter((i) => allowed.has(i.status));
    }
    if (filter?.direction) {
      out = out.filter((i) => i.direction === filter.direction);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, statusKey, directionKey]);

  const getById = useMemo(
    () => (id: string) => all.find((i) => i.id === id),
    [all],
  );

  return {
    invoices,
    add: invoiceStore.add.bind(invoiceStore),
    addMany: invoiceStore.addMany.bind(invoiceStore),
    update: invoiceStore.update.bind(invoiceStore),
    setStatus: invoiceStore.setStatus.bind(invoiceStore),
    remove: invoiceStore.remove.bind(invoiceStore),
    submit: invoiceStore.submit.bind(invoiceStore),
    approve: invoiceStore.approve.bind(invoiceStore),
    reject: invoiceStore.reject.bind(invoiceStore),
    getById,
  };
}
